import { ItemGroup, GroupedSelectMode, SelectedIdsRecord } from './types'

export function getAllItemIds(data: ItemGroup[]): string[] {
  return data.flatMap((group) => group.items.map((item) => item.id))
}

export function filterDataBySearch(data: ItemGroup[], searchText: string): ItemGroup[] {
  if (!searchText) return data
  return data
    .map((group) => {
      const filteredItems = group.items.filter((item) => item.name.toLowerCase().includes(searchText.toLowerCase()))
      return { ...group, items: filteredItems }
    })
    .filter((group) => group.items.length > 0)
}

interface GetSelectedByGroupParams {
  data: ItemGroup[]
  selectedIds: string[]
}

export function getSelectedByGroup({ data, selectedIds }: GetSelectedByGroupParams): SelectedIdsRecord {
  const selectedByGroup: SelectedIdsRecord = {}
  data.forEach((group) => {
    selectedByGroup[group.title] = group.items.filter((item) => selectedIds.includes(item.id)).map((item) => item.id)
  })
  return selectedByGroup
}

// Check if selectedIds contains "all" element
export function hasAllSelected(selectedIds: SelectedIdsRecord, allToggleItemId: string): boolean {
  return selectedIds.all && selectedIds.all.includes(allToggleItemId)
}

interface GetInitialSelectedIdsParams {
  selectedIds: SelectedIdsRecord
}

// Get initial selected IDs
export function getInitialSelectedIds({ selectedIds }: GetInitialSelectedIdsParams): string[] {
  return Object.values(selectedIds).flat()
}

interface HandleItemToggleParams {
  itemId: string
  currentSelectedIds: string[]

  allToggleItemId?: string
}

// Handle item toggle considering "all" logic
export function handleItemToggle({ itemId, currentSelectedIds, allToggleItemId }: HandleItemToggleParams): string[] {
  const isAllItem = allToggleItemId && itemId === allToggleItemId
  const isCurrentlySelected = currentSelectedIds.includes(itemId)

  if (isAllItem) {
    // If "all" is selected, remove selection from all other elements
    if (isCurrentlySelected) {
      return []
    } else {
      // If "all" is being selected, select only it
      return [allToggleItemId]
    }
  } else {
    // Logic for regular elements
    let newSelectedIds = isCurrentlySelected
      ? currentSelectedIds.filter((id) => id !== itemId)
      : [...currentSelectedIds, itemId]

    // If allToggleItemId is in current selection and something else is being selected,
    // remove selection from allToggleItemId
    if (allToggleItemId && currentSelectedIds.includes(allToggleItemId) && !isCurrentlySelected) {
      newSelectedIds = newSelectedIds.filter((id) => id !== allToggleItemId)
    }

    return newSelectedIds
  }
}

interface GetHeaderJustifyContentParams {
  mode: GroupedSelectMode
  onSelectAll?: () => void
}

// Determine justifyContent for header based on mode and presence of onSelectAll
export function getHeaderJustifyContent({ mode, onSelectAll }: GetHeaderJustifyContentParams): string {
  if (mode === 'single') {
    return 'flex-end'
  }

  if (mode === 'multiple') {
    if (onSelectAll) {
      return 'space-between'
    }
    return 'center'
  }

  return 'flex-end'
}
